import {getCurrentUser} from '@/lib/firebaseAuth';

/**
 * Get the results of a completed job search by ID
 * 
 * @param {number} jobId - The ID of the job search
 * @returns {Promise<Object>} - The search results including job listings
 */
export const getSearchResults = async (jobId) => {
  try {
    console.log("getSearchResults: Starting API call with jobId:", jobId);
    
    // Get the current user from Firebase, waiting for auth state to be initialized
    const firebaseUser = await getCurrentUser();
    
    if (!firebaseUser) {
      console.log("No valid Firebase session found");
      throw new Error('Authentication required. Please sign in.');
    }
    
    // Get the Firebase ID token for authentication with the backend
    const idToken = await firebaseUser.getIdToken();
    
    console.log("User authenticated with Firebase, proceeding with search results request");
    console.log("User email from Firebase:", firebaseUser.email);
    
    const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/search/result?id=${jobId}`;
    console.log("Making API request to:", apiUrl);
    
    // Make the API request with the Firebase token
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });
    
    console.log("API response status:", response.status, response.statusText);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error("API Error:", errorData);
      
      // Handle specific error codes with specific error messages
      if (response.status === 401) {
        throw new Error("Your session has expired. Please sign in again to continue.");
      } else if (response.status === 403) {
        throw new Error(errorData.detail || "Access denied. You don't have permission to view these results.");
      } else if (response.status === 404) {
        throw new Error("Job search results not found. The search may still be in progress.");
      } else {
        throw new Error(errorData.detail || 'Failed to fetch search results. Please try again later.');
      }
    }
    
    const data = await response.json();
    console.log("Search results API returned data:", data);
    return data;
  } catch (error) {
    console.error('Error fetching search results:', error);
    throw error;
  }
};

/**
 * Helper function to process API jobs data to match the structure expected by our components
 * 
 * @param {Object} apiResults - Raw API results data
 * @returns {Array} - Processed job data in the format expected by UI components
 */
export const processJobsData = (apiResults) => {
  if (!apiResults || !apiResults.results) {
    return [];
  }

  return Object.entries(apiResults.results)
      .sort(([aId], [bId]) => Number(aId) - Number(bId))
      .map(([id, job]) => {
        const badges = [];

        if (job.remote_mode) {
          const remoteText = job.remote_mode === 'remote' ? 'Remote' :
              job.remote_mode === 'hybrid' ? 'Hybrid' : 'On-site';
          badges.push({ text: remoteText, type: 'default' });
        }

        if (job.employment_type) {
          const formattedType = job.employment_type
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join('-');
          badges.push({ text: formattedType, type: 'default' });
        }

        if (job.seniority_levels) {
          const formattedLevel = job.seniority_levels
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          badges.push({ text: formattedLevel, type: 'default' });
        }

        if (job.match_score) {
          badges.push({ text: `${job.match_score}% Match`, type: 'success' });
        }

        let sections = [];

        if (job.fit_reason) {
          sections.push({
            title: 'Why This Job Matches You',
            content: job.fit_reason
          });
        }

        if (job.sections && typeof job.sections === 'object') {
          Object.entries(job.sections)
              .sort(([aSectionId], [bSectionId]) => Number(aSectionId) - Number(bSectionId))
              .forEach(([_, sectionContent]) => {
                const [sectionTitle, bullets] = Object.entries(sectionContent)[0];
                sections.push({
                  title: sectionTitle,
                  content: Object.entries(bullets)
                      .sort(([aBulletId], [bBulletId]) => Number(aBulletId) - Number(bBulletId))
                      .map(([_, bulletText]) => bulletText)
                });
              });
        }

        const calculateTimeAgo = (postingDate) => {
          if (!postingDate) return 'Recently';
          const now = new Date();
          const postDate = new Date(postingDate);
          const diffTime = Math.abs(now - postDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays === 0) return 'Today';
          if (diffDays === 1) return '1 day ago';
          if (diffDays < 7) return `${diffDays} days ago`;
          if (diffDays < 14) return '1 week ago';
          if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
          if (diffDays < 60) return '1 month ago';
          return `${Math.floor(diffDays / 30)} months ago`;
        };

        return {
          id: Number(id),
          title: job.job_title,
          company: job.company_name,
          location: job.location,
          logo: job.company_logo_url || 'https://via.placeholder.com/100',
          timeAgo: calculateTimeAgo(job.posting_date),
          badges,
          sections,
          matchScore: job.match_score || 0,
          matchReason: job.match_reason || '',
          applyLink: job.apply_link || '#',
        };
      });
};

export const SearchResultsApi = {
  getSearchResults,
  processJobsData
};